interface FontOptions {
  fontFamily: string;
  fontSize: string;
  lineHeight: string;
}

interface GaugeOptions {
  dimension: "width" | "height";
  html?: boolean;
}

type TextGauger = (text: string) => number;

function memoize<Args extends unknown[], R>(
  fn: (...args: Args) => R,
  resolver?: (...args: Args) => unknown,
): (...args: Args) => R {
  const cache = new Map<unknown, R>();
  return (...args: Args): R => {
    const key = resolver ? resolver(...args) : args[0];
    if (!cache.has(key)) {
      cache.set(key, fn(...args));
    }
    return cache.get(key) as R;
  };
}

const measurementDiv = memoize(
  () => {
    const div = document.createElement("div");
    div.className = "DOMMEASUREMENTOUTLET";
    div.style.position = "fixed";
    div.style.top = "-100%";
    div.style.visibility = "hidden";
    div.style.pointerEvents = "none";
    document.body.appendChild(div);
    return div;
  },
  () => "",
);

export const createTextGauger = memoize<[FontOptions, GaugeOptions], TextGauger>(
  ({ fontFamily, fontSize, lineHeight }, { dimension, html }) => {
    if (typeof document === "undefined") {
      return (text) => {
        // SSR approximation; fontSize is a CSS length (e.g. "12px"), parse out the pixel value
        return Number.parseFloat(fontSize) * 0.6 * text.length;
      };
    }
    const element = document.createElement("span");
    element.style.fontFamily = fontFamily;
    element.style.fontSize = fontSize;
    element.style.lineHeight = lineHeight;
    measurementDiv().appendChild(element);
    if (html) {
      return memoize((text) => {
        element.innerHTML = text;
        return element.getBoundingClientRect()[dimension];
      });
    }
    return memoize((text) => {
      element.textContent = text;
      return element.getBoundingClientRect()[dimension];
    });
  },
  ({ fontFamily, fontSize, lineHeight }, { dimension, html }) =>
    [fontFamily, fontSize, lineHeight, dimension, html].join(),
);
